import { ActionSheet } from "@capacitor/action-sheet";
import { AppLauncher } from "@capacitor/app-launcher";
import { Capacitor } from "@capacitor/core";
import { type CSSObject } from "@emotion/react";
import { IonText } from "@ionic/react";
import { DateTime } from "luxon";
import { Link } from "react-router-dom";
import { getBonusDetails } from "src/utils/bonus-pay";
import { Colors, DataItemView, Icon, IconName } from "swing-components";
import { translateRequestSubjectData } from "swing-utils";

import {
  CardHeader,
  IntervalWithTimeZone,
  ModalCancelPolicy,
  ModalDatesTimes,
  ModalPayBreakdown,
  ModalSchoolFeedbackSubmitted,
  useModal,
} from "~components";
import {
  createSkillBuilderTaskURL,
  LanguageRequirementAlert,
  MissingSkillBuilderAlert,
  SpecialEducationInfoAlert,
} from "~pages";
import { SwingConfiguration } from "~skillBuilder/utils/configurations/swing";
import type { SkillBuilderConfiguration } from "~skillBuilder/utils/types";
import {
  formatCityStateZip,
  formatPay,
  formatStringArray,
  generateGoogleMapsLink,
  handleMapLinkNavigation,
  isLongTerm,
  openLinkInApp,
  translate,
  useIsDesktop,
  type RequestData,
} from "~utils";

type RequestDetailCardProps = {
  request: RequestData;
  alert?: JSX.Element;
};

export function RequestDetailCard(props: RequestDetailCardProps) {
  const { request, alert } = props;
  // Note: we have some inconsistencies between mobile and desktop that
  // are more suitable to be solved in JS to avoid rendering the DOM Nodes
  // in addition to conditionally hooking into the sub-component props

  /********* Hooks ********/
  const isDesktop = useIsDesktop();

  /********* Modals ********/
  const { openModal: openPayModal, closeModal: closePayModal } = useModal({
    component: (
      <ModalPayBreakdown
        payment={request.payment}
        isMultiWeek={request.isMultiWeek}
        isMultiDay={request.isMultiDay}
        onDismiss={() => closePayModal()}
      />
    ),
    modalOptions: {
      cssClass: `pay-breakdown-modal`,
    },
  });

  const { openModal: openCancelPolicyModal, closeModal: closeCancelPolicyModal } = useModal({
    component: <ModalCancelPolicy onDismiss={() => closeCancelPolicyModal()} />,
    modalOptions: {
      cssClass: `cancel-policy-modal`,
    },
  });

  const intervalsWithTimezone: IntervalWithTimeZone[] = request.intervals.map(
    (interval: RequestData["intervals"][number]) => {
      return {
        startDate: DateTime.fromISO(interval.startDate).setZone(request.school.timeZone),
        endDate: DateTime.fromISO(interval.endDate).setZone(request.school.timeZone),
      };
    },
  );

  const schoolAddress = {
    street: request?.school?.address?.street ?? "",
    city: request?.school?.address?.city ?? "",
    state: request?.school?.address?.state ?? "",
    zip: request?.school?.address?.zip ?? "",
  };

  const googleMapsLink = generateGoogleMapsLink(schoolAddress);

  const { openModal: openDateTimesModal, closeModal: closeDateTimesModal } = useModal({
    component: (
      <ModalDatesTimes
        intervals={intervalsWithTimezone}
        message={translate("LABEL_TIMEZONE_DISCLAIMER", {
          schoolName: request.school.name,
          timeZone: request.school.timeZone,
        })}
        onDismiss={() => closeDateTimesModal()}
      />
    ),
    modalOptions: {
      cssClass: "dates-times-modal",
    },
  });

  const { openModal: openReviewedFeedbackModal, closeModal: closeReviewedFeedbackModal } = useModal(
    {
      component: (
        <ModalSchoolFeedbackSubmitted
          onDismiss={() => closeReviewedFeedbackModal()}
          feedback={request.schoolFeedback}
        />
      ),
      modalOptions: {
        cssClass: "modal-sub-profile-edit",
      },
    },
  );

  /********* Constants ********/
  const showContactDetails = () => {
    if (!isLongTerm(request)) {
      return true;
    }
    return request.status === "STATUS_FILLED" || request.status === "STATUS_CONFIRMED";
  };

  const specialEdTaskUrl = request.subjects?.includes("SPED") && getSpecialEdTaskUrl();

  const { spedDetails = {} } = request;
  const { supportLevel = [], setting = [], toileting = false } = spedDetails;
  const showSpedDetails = supportLevel?.length > 0 || setting?.length > 0 || toileting;

  /********* Render ********/
  return (
    <div css={{ height: "fit-content", backgroundColor: Colors.white200 }}>
      {/* Only display the alert box inside the card on mobile, on desktop it is displayed outside the card, in RequestDetailsView  */}
      {!isDesktop && alert && <div css={{ padding: "16px 16px 0" }}>{alert}</div>}
      <CardHeader
        requestId={request.id}
        schoolName={request.school.name}
        schoolId={request.school.id}
        status={request.status}
      />
      <div css={styles.divider} />
      <div css={{ padding: "0px 16px 16px" }}>
        <div css={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* MOBILE */}
          {!isDesktop ? (
            <>
              <div css={styles.dateRowWrapper}>
                <div
                  css={styles.dateTimeMobile}
                  onClick={request.isMultiDay ? openDateTimesModal : undefined}
                >
                  <div css={{ display: "grid" }}>
                    <IonText css={styles.dateTimeMobileLabel}>{translate("LABEL_START")}:</IonText>
                    <IonText>
                      {request.displayDate
                        ? request.isMultiDay
                          ? request.displayDate?.split(" - ")[0]
                          : request.displayDate
                        : translate("UNPROVIDED_DATA_MESSAGE")}
                    </IonText>
                  </div>
                  {request.isMultiDay ? (
                    <div css={styles.dateRow}>
                      <div css={{ display: "grid" }}>
                        <IonText css={styles.dateTimeMobileLabel}>
                          {translate("LABEL_END")}:
                        </IonText>
                        <IonText>
                          {request.displayDate
                            ? request.displayDate?.split(" - ")[1]
                            : translate("UNPROVIDED_DATA_MESSAGE")}
                        </IonText>
                      </div>
                      <div>
                        <Icon name="Chevron Right" />
                      </div>
                    </div>
                  ) : (
                    <div css={{ display: "grid" }}>
                      <IonText css={styles.dateTimeMobileLabel}>{translate("LABEL_TIME")}:</IonText>
                      <IonText>
                        {request.displayTime
                          ? request.displayTime
                          : translate("UNPROVIDED_DATA_MESSAGE")}
                      </IonText>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            // DESKTOP
            <>
              <DataItemView label={translate("LABEL_DATE")} icon="Calendar Overlap">
                <div>
                  <IonText>{request.displayDate}</IonText>
                </div>
                {request.isMultiDay ? (
                  <IonText
                    css={{ color: Colors.blue500, cursor: "pointer" }}
                    onClick={openDateTimesModal}
                  >
                    {translate("DATES_TIMES_LABEL")}
                  </IonText>
                ) : (
                  <IonText>{request.displayTime}</IonText>
                )}
              </DataItemView>
            </>
          )}
          {!isDesktop && <div css={styles.dividerMobile} />}
          <PayRow payment={request.payment} isDesktop={isDesktop} openPayModal={openPayModal} />
          {request.payment && !isDesktop && <div css={styles.dividerMobile} />}
          {request.hasFeedback && (
            <>
              <DataItemView
                label={isDesktop ? translate("SCHOOL_FEEDBACK_REVIEWED_LABEL") : ""}
                icon="Check Circle"
                iconColor={Colors.green500}
              >
                {isDesktop ? (
                  <IonText
                    css={{ color: Colors.blue500, cursor: "pointer" }}
                    onClick={openReviewedFeedbackModal}
                  >
                    {translate("SCHOOL_FEEDBACK_VIEW_REVIEW_LABEL")}
                  </IonText>
                ) : (
                  <div css={styles.payRow} onClick={openReviewedFeedbackModal}>
                    <span>{translate("SCHOOL_FEEDBACK_REVIEWED_LABEL")}</span>
                    <Icon name="Chevron Right" />{" "}
                  </div>
                )}
              </DataItemView>
              {!isDesktop && <div css={styles.dividerMobile} />}
            </>
          )}
          <DataItemView label={isDesktop ? translate("LABEL_ADDRESS") : ""} icon="Location">
            {request.school.address ? (
              <a
                css={{ display: "flex", flexDirection: "column" }}
                target="_blank"
                rel="noreferrer"
                href={googleMapsLink}
                onClick={(e) =>
                  handleMapLinkNavigation(e, {
                    getPlatform: Capacitor.getPlatform,
                    appLauncher: AppLauncher,
                    actionSheet: ActionSheet,
                    openLinkInApp: openLinkInApp,
                    query: encodeURIComponent(
                      `${schoolAddress.street} ${schoolAddress.city}, ${schoolAddress.state}, ${schoolAddress.zip}`,
                    ),
                    link: googleMapsLink,
                  })
                }
              >
                {request.school.address.street && (
                  <IonText css={styles.truncateOneLine}>{request.school.address.street}</IonText>
                )}
                <IonText css={styles.truncateOneLine}>
                  {formatCityStateZip(request.school.address)}
                </IonText>
              </a>
            ) : (
              <IonText>{translate("UNPROVIDED_DATA_MESSAGE")}</IonText>
            )}
          </DataItemView>
          {showContactDetails() && (
            <DataItemView label={translate("LABEL_CONTACT")} icon="Phone">
              {request.contact ? (
                <div css={{ display: "flex", flexDirection: "column" }}>
                  <IonText>
                    {request.contact.name} -{" "}
                    <a href={`tel:${request.contact.phoneNumber}`}>{request.contact.phoneNumber}</a>
                  </IonText>
                  <IonText>
                    <a href={`mailto:${request.contact.email}`}>{request.contact.email}</a>
                  </IonText>
                </div>
              ) : (
                <IonText>{translate("UNPROVIDED_DATA_MESSAGE")}</IonText>
              )}
            </DataItemView>
          )}
          {(request.teacher || request.positionData?.displayName) && (
            <DataItemView
              label={request.positionData?.displayName ? "Position" : "Teacher"}
              icon="Teacher"
            >
              <>
                {request.positionData?.displayName && (
                  <IonText>{request.positionData.displayName}</IonText>
                )}
                {request.teacher && <IonText>{request.teacher}</IonText>}
              </>
            </DataItemView>
          )}
          <DataItemView label={translate("LABEL_GRADE")} icon="Grad Cap">
            <IonText>
              {request.grades
                ? formatStringArray(request.grades)
                : translate("UNPROVIDED_DATA_MESSAGE")}
            </IonText>
          </DataItemView>
          <DataItemView label={translate("LABEL_SUBJECT")} icon="Book">
            <IonText>
              {request.subjects ? (
                <>
                  {formatStringArray(
                    translateRequestSubjectData({
                      subjects: request.subjects,
                    }),
                  )}
                  {specialEdTaskUrl && (
                    <p css={{ margin: 0 }}>
                      {translate("REQUEST_CARD_DETAIL_SPED_SKILL_BUILDER_PREP")}{" "}
                      <Link to={specialEdTaskUrl}>
                        {translate("REQUEST_CARD_DETAIL_SPED_SKILL_BUILDER_PREP_HYPERLINK")}
                      </Link>
                      .
                    </p>
                  )}
                </>
              ) : (
                translate("UNPROVIDED_DATA_MESSAGE")
              )}
            </IonText>
          </DataItemView>
          {showSpedDetails && (
            <DataItemView icon="Apple" label={translate("LABEL_SPED")}>
              <div>
                {setting?.length > 0 && (
                  <p css={{ margin: 0 }}>
                    {setting.map((item) => translate(`LABEL_SPED_SETTING_${item}`)).join(", ")}
                  </p>
                )}
                {supportLevel?.length > 0 && (
                  <p css={{ margin: 0 }}>
                    {supportLevel.map((item) => translate(`LABEL_SPED_SUPPORT_${item}`)).join(", ")}
                  </p>
                )}
                {toileting && translate("LABEL_TOILETING_REQUIRED")}
              </div>
            </DataItemView>
          )}
          {request.bilingualRequirements && request.bilingualRequirements.length > 0 && (
            <DataItemView label={translate("LABEL_BILINGUAL_REQUIREMENT")} icon="Website">
              <IonText>{formatStringArray(request.bilingualRequirements)}</IonText>
            </DataItemView>
          )}
          <DataItemView label={translate("LABEL_LESSON_PLAN")} icon="Clipboard">
            <IonText>
              {request.isLessonPlanProvided ? (
                translate("LESSON_PLAN_PROVIDED")
              ) : (
                <p
                  css={{ margin: 0 }}
                  dangerouslySetInnerHTML={{
                    __html: translate("LESSON_PLAN_NOT_PROVIDED_SUB_PLAN_AI"),
                  }}
                />
              )}
            </IonText>
          </DataItemView>
          <SimpleDataDisplay label={translate("LABEL_NOTES")} icon="Note" data={request.memo} />
          {/* On desktop, show alerts in priority order: missing skill builders > language requirement > special education
          On mobile, display logic is handled in the RequestDetailsView above the action buttons */}
          {isDesktop
            ? [
                request.missingSkillbuilderAttrs &&
                  request.missingSkillbuilderAttrs.length > 0 &&
                  request.status === "STATUS_OPEN" && (
                    <MissingSkillBuilderAlert
                      missingSkillBuilderAttrs={request.missingSkillbuilderAttrs}
                    />
                  ),
                request.bilingualRequirements &&
                  request.bilingualRequirements.length > 0 &&
                  request.status === "STATUS_OPEN" && (
                    <LanguageRequirementAlert languages={request.bilingualRequirements!} />
                  ),
                request.subjects?.includes("SPED") && (
                  <SpecialEducationInfoAlert spedDetails={request.spedDetails} />
                ),
              ].filter(Boolean)
            : null}
          {request.status === "STATUS_CONFIRMED" && (
            <CancelPolicyButton onClick={openCancelPolicyModal} />
          )}
        </div>
      </div>
    </div>
  );
}

/********* Helper Components ********/
type SimpleDisplayDataProps = {
  label: string;
  icon: IconName;
  data: string | number | undefined;
};

function SimpleDataDisplay(props: SimpleDisplayDataProps) {
  const { label, icon, data } = props;

  return (
    <DataItemView label={label} icon={icon}>
      <IonText>{data ? `${data}` : translate("UNPROVIDED_DATA_MESSAGE")}</IonText>
    </DataItemView>
  );
}
type CancelPolicyButtonProps = { onClick: () => void };

export function CancelPolicyButton(props: CancelPolicyButtonProps) {
  const { onClick } = props;

  return (
    <div
      onClick={onClick}
      css={{
        cursor: "pointer",
        display: "flex",
        flexDirection: "row",
        backgroundColor: Colors.white300,
        border: "solid",
        borderColor: Colors.slate300,
        borderRadius: 4,
        borderWidth: 1,
        padding: 8,
        justifyContent: "space-between",
      }}
    >
      <div css={{ display: "flex", flexDirection: "row" }}>
        <Icon name="Block" />
        <div css={{ display: "flex", flexDirection: "column", marginLeft: 8 }}>
          <IonText css={styles.label}>{translate("CANCEL_POLICY_LABEL")}</IonText>
          <IonText>{translate("CANCEL_POLICY_BUTTON_SUBHEADING")}</IonText>
        </div>
      </div>
      <Icon name="Chevron Right" />
    </div>
  );
}
/********* Helper Functions ********/
export function getSpecialEdTaskUrl(config: SkillBuilderConfiguration = SwingConfiguration) {
  const specialEdGroup = config.groups.find(
    (group) => group.name === "Special Education Classrooms",
  );

  if (!specialEdGroup) return;

  const firstTaskIndex = config.groups
    .flatMap((group) => group.tasks)
    .findIndex((task) => task === specialEdGroup.tasks[0]);

  return firstTaskIndex !== -1
    ? `${createSkillBuilderTaskURL(config.version, firstTaskIndex)}`
    : undefined;
}

type PayRowProps = {
  payment: RequestData["payment"];
  isDesktop: boolean;
  openPayModal: () => void;
};

export function PayRow(props: PayRowProps) {
  const { payment, isDesktop, openPayModal } = props;
  const { totalPayValue } = payment ?? {};
  const { hasRequestBonus, bonusText, bonusHourlyPay } = getBonusDetails(payment);
  const requestBonusText = `${bonusText} extra pay included in the amount shown.`;

  if (payment?.showHourlyRate) {
    return (
      <div css={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <DataItemView label={isDesktop ? translate("LABEL_PAY") : ""} icon="Monetization">
          <div css={!isDesktop && styles.payRow} onClick={!isDesktop ? openPayModal : undefined}>
            <p css={{ margin: 0 }}>
              {totalPayValue && totalPayValue > 0 ? (
                <>
                  {translate("LABEL_ESTIMATED_TOTAL")}:{" "}
                  {totalPayValue && formatPay(false, totalPayValue)}{" "}
                </>
              ) : null}
              {bonusHourlyPay && bonusHourlyPay > 0 ? (
                <>({formatPay(true, bonusHourlyPay)})</>
              ) : null}
            </p>
            {!isDesktop && <Icon name="Chevron Right" />}
            {isDesktop && (
              <p
                css={{ margin: 0, color: Colors.blue500, cursor: "pointer" }}
                onClick={openPayModal}
              >
                {translate("PAY_BREAKDOWN_LABEL")}
              </p>
            )}
          </div>
        </DataItemView>
        {hasRequestBonus && (
          <div
            css={{
              backgroundColor: "#F0FDF4",
              border: `1px solid ${Colors.green500}`,
              borderRadius: 4,
              padding: "8px 12px",
            }}
          >
            <p css={[{ margin: 0 }, styles.text]}>{requestBonusText}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <DataItemView label={isDesktop ? translate("LABEL_PAY") : ""} icon="Monetization">
      <div css={styles.payRow} onClick={!isDesktop ? openPayModal : undefined}>
        <IonText>
          {totalPayValue || payment?.payValue
            ? formatPay(false, totalPayValue ?? payment?.payValue ?? 0)
            : translate("UNPROVIDED_DATA_MESSAGE")}
        </IonText>
        {!isDesktop && <Icon name="Chevron Right" />}
      </div>
      {isDesktop && (
        <IonText css={{ color: Colors.blue500, cursor: "pointer" }} onClick={openPayModal}>
          {translate("PAY_BREAKDOWN_LABEL")}
        </IonText>
      )}
    </DataItemView>
  );
}

/********* Styles ********/
const styles = {
  divider: {
    width: "calc(100% - 32px)", // account for container 16px padding
    height: 1,
    margin: "16px auto",
    backgroundColor: "rgba(0, 0, 0, 0.1)",
  },
  dividerMobile: {
    width: "100%",
    height: 1,
    margin: "0 auto",
    backgroundColor: "rgba(0, 0, 0, 0.1)",
  },
  dateTimeMobile: {
    display: "grid",
    gridTemplateColumns: "1fr 2fr",
    color: Colors.black,
  },
  dateTimeMobileLabel: {
    fontWeight: 600,
  },
  dateRowWrapper: {
    position: "relative",
  },
  dateRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  label: {
    gridArea: "label",
    fontWeight: 600,
    fontSize: 14,
    lineHeight: "20px",
    "@media (min-width: 900px)": {
      fontSize: 16,
      lineHeight: "24px",
    },
  },
  truncateOneLine: {
    WebkitLineClamp: 1,
    WebkitBoxOrient: "vertical",
    display: "-webkit-box",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "normal",
  },
  payRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  text: {
    fontWeight: 400,
    fontSize: 14,
    lineHeight: "20px",
    "@media (min-width: 900px)": {
      fontSize: 16,
      lineHeight: "24px",
    },
  },
} satisfies Record<string, CSSObject>;
